import { type ZslSchemas } from "@zapier/zsl-schema";
import { FALLBACK_IMPLEMENTATION_IDS } from "@zapier/canvas-constants";
import { uuid } from "@zapier/canvas-state";

const NODE_POSITION_OFFSET_Y = 400;
const BLOCK_TYPE_TO_NODE_TITLE: Record<string, string> = {
  "form-block": "User fills out form",
  "table-listing-block": "View on table",
  "kanban-block": "Manage on board",
  "chatbot-block": "User chats with bot",
};

// Node type to X position mapping
const NODE_TYPE_X_POSITIONS = {
  zap: 0,
  interface: 400,
  table: 800,
  chatbot: -400,
  agent: 0,
};

export type ZSL = ZslSchemas["ZslTemplate"];
type ZDLStep = ZslSchemas["zaps.ZdlStep"];

interface CanvasData {
  canvas: {
    name: string;
    description: string;
    nodes: Array<{
      id: string;
      type: string;
      position: { x: number; y: number };
      data: Record<string, any>;
      style?: Record<string, any>;
      measured?: { width: number; height: number };
      parentId?: string;
      selectable?: boolean;
      deletable?: boolean;
      draggable?: boolean;
      connectable?: boolean;
      targetPosition?: string;
      sourcePosition?: string;
      hidden?: boolean;
    }>;
    edges: Array<{
      id: string;
      source: string;
      target: string;
      type?: string;
      sourceHandle?: string;
      targetHandle?: string;
      markerEnd?: {
        type: string;
        strokeWidth: number;
        color: string;
      };
      deletable?: boolean;
      focusable?: boolean;
      selectable?: boolean;
      reconnectable?: boolean;
      animated?: boolean;
      hidden?: boolean;
    }>;
  };
  warnings: string[];
}

export function parseCanvasDataFromZSL(
  zsl: ZSL,
  existingCanvas?: CanvasData,
): CanvasData {
  const data: CanvasData = {
    canvas: {
      name: zsl.name || "",
      description: zsl.description || "",
      nodes: [],
      edges: [],
    },
    warnings: [],
  };

  // Copy warnings from existing canvas if provided
  if (existingCanvas) {
    data.warnings = [...existingCanvas.warnings];
  }

  const { nodes, edges } = data.canvas;
  const { warnings } = data;
  const assetIdsToNodeIds: Record<string, string> = {};

  // Track assets in ZSL to know what should exist in the canvas
  const zslAssets = new Map<string, { type: string; id: string }>();

  // Register ZSL assets
  (zsl.zaps || []).forEach((zap) => {
    zslAssets.set(zap.id, { type: "zap", id: zap.id });
  });

  (zsl.tables || []).forEach((table) => {
    zslAssets.set(table.id, { type: "table", id: table.id });
  });

  (zsl.chatbots || []).forEach((chatbot) => {
    zslAssets.set(chatbot.id, { type: "chatbot", id: chatbot.id });
  });

  ((zsl as any).agents || []).forEach((agent: any) => {
    zslAssets.set(agent.id, { type: "agent", id: agent.id });
  });

  (zsl.interfaces || []).forEach((interface_) => {
    (interface_.pages || []).forEach((page) => {
      const interfaceAssetId = `${interface_.id}.${page.id}`;
      zslAssets.set(interfaceAssetId, {
        type: "interface",
        id: interfaceAssetId,
      });
    });
  });

  // Store existing edge styles for reuse
  const existingEdgeStyles: Record<string, any> = {};
  if (existingCanvas?.canvas.edges) {
    for (const edge of existingCanvas.canvas.edges) {
      // Only store standard edges, not special system edges like zapStepEdge
      if (!edge.type || edge.type === "default") {
        existingEdgeStyles[`${edge.source}=>${edge.target}`] = {
          sourceHandle: edge.sourceHandle,
          targetHandle: edge.targetHandle,
          markerEnd: edge.markerEnd,
        };
      }
    }
  }

  // Calculate the maximum Y position for each node type from existing canvas
  const maxYPositions = calculateMaxYPositions(existingCanvas);

  // Re-use existing nodes when possible
  if (existingCanvas && existingCanvas.canvas.nodes) {
    // First, properly map existing nodes to asset IDs
    for (const existingNode of existingCanvas.canvas.nodes) {
      // Skip text nodes as they'll be recreated
      if (existingNode.type === "text") continue;

      let assetId = "";
      let assetType = "";

      if (existingNode.type === "zap" && existingNode.data.zapId) {
        assetId = existingNode.data.zapId;
        assetType = "zap";
      } else if (existingNode.data.tableId) {
        assetId = existingNode.data.tableId;
        assetType = "table";
      } else if (existingNode.data.chatbotId) {
        assetId = existingNode.data.chatbotId;
        assetType = "chatbot";
      } else if (existingNode.data.projectId && existingNode.data.pageId) {
        assetId = `${existingNode.data.projectId}.${existingNode.data.pageId}`;
        assetType = "interface";
      } else if (existingNode.data.agentId) {
        assetId = existingNode.data.agentId;
        assetType = "agent";
      }

      // Only keep nodes that still exist in ZSL
      if (assetId && zslAssets.has(assetId)) {
        const nodeData = { ...existingNode };

        // Update node label if needed
        if (assetType === "zap") {
          const zap = zsl.zaps?.find((z) => z.id === assetId);
          if (zap) nodeData.data = { ...nodeData.data, label: zap.title };
        } else if (assetType === "table") {
          const table = zsl.tables?.find((t) => t.id === assetId);
          if (table) nodeData.data = { ...nodeData.data, label: table.name };
        } else if (assetType === "chatbot") {
          const chatbot = zsl.chatbots?.find((c) => c.id === assetId);
          if (chatbot)
            nodeData.data = {
              ...nodeData.data,
              label: chatbot.config?.chatbotName,
            };
        } else if (assetType === "interface") {
          const [interfaceId, pageId] = assetId.split(".");
          const interface_ = zsl.interfaces?.find((i) => i.id === interfaceId);
          const page = interface_?.pages?.find((p) => p.id === pageId);
          if (interface_ && page) {
            // Use page.title as label if available, fallback to interface name or page name
            nodeData.data = {
              ...nodeData.data,
              label:
                page.title ||
                interface_.name ||
                page.name ||
                nodeData.data.label,
            };
          }
        } else if (assetType === "agent") {
          const agent = (zsl as any).agents?.find((a: any) => a.id === assetId);
          if (agent) nodeData.data = { ...nodeData.data, label: agent.name };
        }

        nodes.push(nodeData);
        assetIdsToNodeIds[assetId] = nodeData.id;

        // Remove from map so we don't add it again
        zslAssets.delete(assetId);
      }
    }
  }

  // Add remaining nodes that weren't in the existing canvas
  const remainingZapIds = Array.from(zslAssets.values())
    .filter((asset) => asset.type === "zap")
    .map((asset) => asset.id);

  const remainingTableIds = Array.from(zslAssets.values())
    .filter((asset) => asset.type === "table")
    .map((asset) => asset.id);

  const remainingChatbotIds = Array.from(zslAssets.values())
    .filter((asset) => asset.type === "chatbot")
    .map((asset) => asset.id);

  const remainingInterfaceIds = Array.from(zslAssets.values())
    .filter((asset) => asset.type === "interface")
    .map((asset) => asset.id);

  const remainingAgentIds = Array.from(zslAssets.values())
    .filter((asset) => asset.type === "agent")
    .map((asset) => asset.id);

  addZapNodes(
    zsl,
    nodes,
    assetIdsToNodeIds,
    remainingZapIds,
    maxYPositions.zap,
  );
  addTableNodes(
    zsl,
    nodes,
    assetIdsToNodeIds,
    remainingTableIds,
    maxYPositions.table,
  );
  addChatbotNodes(
    zsl,
    nodes,
    assetIdsToNodeIds,
    remainingChatbotIds,
    maxYPositions.chatbot,
  );
  addInterfaceNodes(
    zsl,
    nodes,
    assetIdsToNodeIds,
    remainingInterfaceIds,
    maxYPositions.interface,
  );
  addAgentNodes(
    zsl,
    nodes,
    assetIdsToNodeIds,
    remainingAgentIds,
    maxYPositions.agent,
  );

  // Regenerate all edges
  addZapEdges(zsl, edges, assetIdsToNodeIds, warnings);
  addTableEdges(zsl, edges, assetIdsToNodeIds, warnings);
  addChatbotEdges(zsl, edges, assetIdsToNodeIds, warnings);
  addInterfaceEdges(zsl, edges, assetIdsToNodeIds, warnings);

  // Add remaining specialized edges
  preserveSpecializedEdges(existingCanvas, edges, nodes);

  // Apply saved edge styles
  restoreEdgeStyles(edges, existingEdgeStyles);

  return data;
}

function calculateMaxYPositions(
  existingCanvas?: CanvasData,
): Record<string, number> {
  // Initialize with base positions to create a vertical layout staggering
  const maxPositions = {
    zap: 0,
    table: 0,
    chatbot: 0,
    interface: 0,
    agent: 0,
  };

  if (!existingCanvas || !existingCanvas.canvas.nodes) {
    return maxPositions;
  }

  // Group nodes by their X position ranges (columns)
  const columnGroups: Record<
    number,
    Array<{
      id: string;
      position: { x: number; y: number };
      height: number;
      type: string;
    }>
  > = {};

  // Determine column groups with 300px spans
  const COLUMN_WIDTH = 300;

  // First pass: group nodes by column and find max Y positions
  for (const node of existingCanvas.canvas.nodes) {
    if (node.type === "text") continue;

    const nodeX = node.position?.x ?? 0;
    const nodeY = node.position?.y ?? 0;
    const nodeHeight = node.measured?.height ?? 300;

    // Determine node type (for fallback to original behavior)
    let nodeType = "other";
    if (node.type === "zap") nodeType = "zap";
    else if (node.data.tableId) nodeType = "table";
    else if (node.data.chatbotId) nodeType = "chatbot";
    else if (node.data.projectId) nodeType = "interface";
    else if (node.data.agentId) nodeType = "agent";

    // Get the column this node belongs to
    const columnKey = Math.floor(nodeX / COLUMN_WIDTH) * COLUMN_WIDTH;

    // Initialize column if it doesn't exist
    if (!columnGroups[columnKey]) {
      columnGroups[columnKey] = [];
    }

    // Add node to its column group
    columnGroups[columnKey].push({
      id: node.id,
      position: { x: nodeX, y: nodeY },
      height: nodeHeight,
      type: nodeType,
    });

    // Also update the type-specific maximum positions for backward compatibility
    if (nodeType in maxPositions) {
      maxPositions[nodeType as keyof typeof maxPositions] = Math.max(
        maxPositions[nodeType as keyof typeof maxPositions],
        nodeY + nodeHeight + 50, // 50px buffer
      );
    }
  }

  // For each X position in NODE_TYPE_X_POSITIONS
  Object.entries(NODE_TYPE_X_POSITIONS).forEach(([nodeType, xPos]) => {
    // Find the column this position belongs to
    const columnKey = Math.floor(xPos / COLUMN_WIDTH) * COLUMN_WIDTH;

    // If there are nodes in this column
    if (columnGroups[columnKey] && columnGroups[columnKey].length > 0) {
      // Find the maximum Y position of nodes in this column
      const columnMaxY = columnGroups[columnKey].reduce((maxY, node) => {
        return Math.max(maxY, node.position.y + node.height);
      }, 0);

      // Update the maximum Y position for this node type
      // Only if the column has nodes and their max Y is greater than current
      if (nodeType in maxPositions && columnMaxY > 0) {
        maxPositions[nodeType as keyof typeof maxPositions] = Math.max(
          maxPositions[nodeType as keyof typeof maxPositions],
          columnMaxY + 100, // 100px buffer
        );
      }
    }

    // Also check neighboring columns for potential overlaps
    const neighborColumns = [
      columnKey - COLUMN_WIDTH,
      columnKey + COLUMN_WIDTH,
    ];
    neighborColumns.forEach((neighborKey) => {
      if (columnGroups[neighborKey] && columnGroups[neighborKey].length > 0) {
        // Check if there are nodes in neighboring column close to this X position
        const closeNodes = columnGroups[neighborKey].filter(
          (node) => Math.abs(node.position.x - xPos) < COLUMN_WIDTH,
        );

        if (closeNodes.length > 0) {
          // Find max Y of close nodes
          const closeMaxY = closeNodes.reduce((maxY, node) => {
            return Math.max(maxY, node.position.y + node.height);
          }, 0);

          // Update position if neighboring nodes would overlap
          if (nodeType in maxPositions && closeMaxY > 0) {
            maxPositions[nodeType as keyof typeof maxPositions] = Math.max(
              maxPositions[nodeType as keyof typeof maxPositions],
              closeMaxY + 100, // 100px buffer
            );
          }
        }
      }
    });
  });

  return maxPositions;
}

function addZapNodes(
  zsl: ZSL,
  nodes: CanvasData["canvas"]["nodes"],
  assetIdsToNodeIds: Record<string, string>,
  remainingZapIds: string[] = [],
  startYPosition: number = 0,
): void {
  const nodePositionX = NODE_TYPE_X_POSITIONS.zap;
  let nodePositionY = startYPosition;
  const zaps = zsl.zaps || [];

  // Add only remaining zaps that weren't preserved from existing canvas
  const zapsToAdd = remainingZapIds.length
    ? zaps.filter((zap) => remainingZapIds.includes(zap.id))
    : [];

  for (const zap of zapsToAdd) {
    const zapId = zap.id;
    const nodeId = uuid();
    assetIdsToNodeIds[zapId] = nodeId;

    // Find a position for this node that doesn't overlap with existing nodes
    const position = findNonOverlappingPosition(
      nodes,
      nodePositionX,
      nodePositionY,
    );

    nodes.push({
      id: nodeId,
      type: "zap",
      position: position,
      data: { zapId, label: zap.title },
    });

    // Update the Y position for the next node, adding standard offset
    nodePositionY = position.y + NODE_POSITION_OFFSET_Y;
  }
}

function addTableNodes(
  zsl: ZSL,
  nodes: CanvasData["canvas"]["nodes"],
  assetIdsToNodeIds: Record<string, string>,
  remainingTableIds: string[] = [],
  startYPosition: number = 0,
): void {
  const nodePositionX = NODE_TYPE_X_POSITIONS.table;
  let nodePositionY = startYPosition;
  const tables = zsl.tables || [];

  // Add only remaining tables that weren't preserved from existing canvas
  // If remainingTableIds is empty and was explicitly provided, don't add any tables
  const tablesToAdd = remainingTableIds.length
    ? tables.filter((table) => remainingTableIds.includes(table.id))
    : [];

  for (const table of tablesToAdd) {
    const tableId = table.id;
    const nodeId = uuid();
    assetIdsToNodeIds[tableId] = nodeId;

    // Find a position for this node that doesn't overlap with existing nodes
    const position = findNonOverlappingPosition(
      nodes,
      nodePositionX,
      nodePositionY,
    );

    nodes.push({
      id: nodeId,
      type: "default",
      position: position,
      data: {
        tableId,
        label: table.name,
        app: "Tables by Zapier",
        services: [{ currentImplementation: { id: "TableCLIAPI@1.15.12" } }],
      },
    });

    // Update the Y position for the next node, adding standard offset
    nodePositionY = position.y + NODE_POSITION_OFFSET_Y;
  }
}

function addChatbotNodes(
  zsl: ZSL,
  nodes: CanvasData["canvas"]["nodes"],
  assetIdsToNodeIds: Record<string, string>,
  remainingChatbotIds: string[] = [],
  startYPosition: number = 0,
): void {
  const nodePositionX = NODE_TYPE_X_POSITIONS.chatbot;
  let nodePositionY = startYPosition;
  const chatbots = zsl.chatbots || [];

  // Add only remaining chatbots that weren't preserved from existing canvas
  const chatbotsToAdd = remainingChatbotIds.length
    ? chatbots.filter((chatbot) => remainingChatbotIds.includes(chatbot.id))
    : [];

  for (const chatbot of chatbotsToAdd) {
    const chatbotId = chatbot.id;
    const nodeId = uuid();
    assetIdsToNodeIds[chatbotId] = nodeId;

    // Find a position for this node that doesn't overlap with existing nodes
    const position = findNonOverlappingPosition(
      nodes,
      nodePositionX,
      nodePositionY,
    );

    nodes.push({
      id: nodeId,
      type: "default",
      position: position,
      data: {
        chatbotId,
        label: chatbot.config?.chatbotName,
        app: "Zapier Chatbots",
        services: [{ currentImplementation: { id: "ChatbotsCLIAPI@0.0.10" } }],
      },
    });

    // Update the Y position for the next node, adding standard offset
    nodePositionY = position.y + NODE_POSITION_OFFSET_Y;
  }
}

function addInterfaceNodes(
  zsl: ZSL,
  nodes: CanvasData["canvas"]["nodes"],
  assetIdsToNodeIds: Record<string, string>,
  remainingInterfaceIds: string[] = [],
  startYPosition: number = 0,
): void {
  const nodePositionX = NODE_TYPE_X_POSITIONS.interface;
  let nodePositionY = startYPosition;
  const interfaces = zsl.interfaces || [];

  // If remainingInterfaceIds is empty and was explicitly provided, don't process any interfaces
  if (remainingInterfaceIds.length === 0 && arguments.length > 3) {
    return;
  }

  for (const interface_ of interfaces) {
    const interfaceId = interface_.id;
    const pages = interface_.pages || [];

    for (const page of pages) {
      const pageAssetId = `${interfaceId}.${page.id}`;

      // Skip if not in remaining interfaces to add
      if (
        remainingInterfaceIds.length &&
        !remainingInterfaceIds.includes(pageAssetId)
      ) {
        continue;
      }

      let nodeTitle = page.title;
      if (!nodeTitle) {
        const blocks = page.blocks || [];
        for (const block of blocks) {
          const blockType = block.type || "";
          nodeTitle = BLOCK_TYPE_TO_NODE_TITLE[blockType] || "";
          if (nodeTitle) break;
        }
      }

      const pageId = page.id;
      const nodeId = uuid();
      assetIdsToNodeIds[pageAssetId] = nodeId;

      // Find a position for this node that doesn't overlap with existing nodes
      const position = findNonOverlappingPosition(
        nodes,
        nodePositionX,
        nodePositionY,
      );

      nodes.push({
        id: nodeId,
        type: "default",
        position: position,
        data: {
          projectId: interfaceId,
          pageId,
          label: nodeTitle,
          app: "Interfaces by Zapier",
          services: [
            { currentImplementation: { id: "InterfacesCLIAPI@1.3.4" } },
          ],
        },
      });

      // Update the Y position for the next node, adding standard offset
      nodePositionY = position.y + NODE_POSITION_OFFSET_Y;
    }
  }
}

function addAgentNodes(
  zsl: ZSL,
  nodes: CanvasData["canvas"]["nodes"],
  assetIdsToNodeIds: Record<string, string>,
  remainingAgentIds: string[] = [],
  startYPosition: number = 0,
): void {
  const nodePositionX = NODE_TYPE_X_POSITIONS.agent;
  let nodePositionY = startYPosition;
  const agents = (zsl as any).agents || [];

  // Add only remaining agents that weren't preserved from existing canvas
  const agentsToAdd = remainingAgentIds.length
    ? agents.filter((agent: any) => remainingAgentIds.includes(agent.id))
    : [];

  for (const agent of agentsToAdd) {
    const agentId = agent.id;
    const nodeId = uuid();
    assetIdsToNodeIds[agentId] = nodeId;

    // Find a position for this node that doesn't overlap with existing nodes
    const position = findNonOverlappingPosition(
      nodes,
      nodePositionX,
      nodePositionY,
    );

    nodes.push({
      id: nodeId,
      type: "default",
      position: position,
      data: {
        agentId,
        label: (agent as any).name || "Agent",
        app: "Zapier Agents",
        services: [
          { currentImplementation: { id: FALLBACK_IMPLEMENTATION_IDS.AGENTS } },
        ],
      },
    });

    // Update the Y position for the next node, adding standard offset
    nodePositionY = position.y + NODE_POSITION_OFFSET_Y;
  }
}

function addZapEdges(
  zsl: ZSL,
  edges: CanvasData["canvas"]["edges"],
  assetIdsToNodeIds: Record<string, string>,
  warnings: string[],
): void {
  const zaps = zsl.zaps || [];
  for (const zap of zaps) {
    const zapId = zap.id;
    const steps = zap.zdl?.steps || [];

    // Create a set to track tables that have already been connected to this zap
    const connectedTableIds = new Set<string>();

    // Check for table triggers in zap steps
    for (const step of steps) {
      // Check if this is a trigger step for a new record or updated record in a table
      if (step.app?.includes("TableCLIAPI")) {
        const isTriggeredByTable =
          step.action === "new_record" ||
          step.action === "updated_record" ||
          step.action === "new_or_updated";

        if (isTriggeredByTable) {
          // Connect to a specific table if it's mentioned in the step params
          if (step.params?.table_id) {
            const tableId = step.params.table_id as string;
            if (!connectedTableIds.has(tableId)) {
              addEdge(edges, assetIdsToNodeIds, tableId, zapId, warnings);
              connectedTableIds.add(tableId);
            }
          }
          // If no specific table is mentioned but there's likely a table trigger,
          // connect to each table as a fallback
          else if (zsl.tables && zsl.tables.length > 0) {
            for (const table of zsl.tables) {
              const tableId = table.id;
              if (!connectedTableIds.has(tableId)) {
                addEdge(edges, assetIdsToNodeIds, tableId, zapId, warnings);
                connectedTableIds.add(tableId);
              }
            }
          }
        }
      }

      // Existing logic for explicit table connections
      if (step.params?.table_id) {
        const tableId = step.params?.table_id as string;
        if (!connectedTableIds.has(tableId)) {
          addEdge(edges, assetIdsToNodeIds, tableId, zapId, warnings);
          connectedTableIds.add(tableId);
        }
      }
      if (step.params?.pageId) {
        const pageId = step.params?.pageId as string;
        addEdge(edges, assetIdsToNodeIds, pageId, zapId, warnings);
      }
      const stepsInner1 = step.steps || [];
      for (const stepInner1 of stepsInner1) {
        const stepsInner2 = (stepInner1.steps || []) as ZDLStep[];
        for (const stepInner2 of stepsInner2) {
          if (stepInner2.params?.table_id) {
            const tableId = stepInner2.params?.table_id as string;
            if (!connectedTableIds.has(tableId)) {
              addEdge(edges, assetIdsToNodeIds, tableId, zapId, warnings);
              connectedTableIds.add(tableId);
            }
          }
        }
      }
    }
  }
}

function addChatbotEdges(
  zsl: ZSL,
  edges: CanvasData["canvas"]["edges"],
  assetIdsToNodeIds: Record<string, string>,
  warnings: string[],
): void {
  const chatbots = zsl.chatbots || [];
  for (const chatbot of chatbots) {
    const chatbotId = chatbot.id;

    // Create edge from chatbot to table for data collection
    const triggers = chatbot.config?.triggers || [];
    for (const trigger of triggers) {
      if (trigger.config?.collectUserDataTableId) {
        const tableId = trigger.config?.collectUserDataTableId as string;
        addEdge(edges, assetIdsToNodeIds, chatbotId, tableId, warnings);
      }
    }

    // Create edge from table to chatbot for knowledge sources
    const knowledgeSources = Array.isArray(chatbot.config?.knowledgeSources)
      ? chatbot.config.knowledgeSources
      : [];
    for (const source of knowledgeSources) {
      if (source.type === "table" && source.reference) {
        const tableId = source.reference.replace(
          /\${{\s*tables\.(\S+)\s*}}/,
          "$1",
        );
        addEdge(edges, assetIdsToNodeIds, tableId, chatbotId, warnings);
      }
    }
  }
}

function addTableEdges(
  zsl: ZSL,
  edges: CanvasData["canvas"]["edges"],
  assetIdsToNodeIds: Record<string, string>,
  warnings: string[],
): void {
  const tables = zsl.tables || [];
  for (const table of tables) {
    const tableId = table.id;
    const fields = table.fields || [];
    for (const field of fields) {
      if (
        field.options?.choice_provider &&
        typeof field.options.choice_provider === "object" &&
        "with_table_id" in field.options.choice_provider
      ) {
        const targetTableId = field.options.choice_provider
          .with_table_id as string;
        addEdge(edges, assetIdsToNodeIds, tableId, targetTableId, warnings);
      }
    }
  }
}

function addInterfaceEdges(
  zsl: ZSL,
  edges: CanvasData["canvas"]["edges"],
  assetIdsToNodeIds: Record<string, string>,
  warnings: string[],
): void {
  const interfaces = zsl.interfaces || [];
  for (const interface_ of interfaces) {
    const interfaceId = interface_.id;
    const pages = interface_.pages || [];
    for (const page of pages) {
      const pageId = page.id;
      const sourceAssetId = `${interfaceId}.${pageId}`;
      const blocks = page.blocks || [];
      for (const block of blocks) {
        const config = block.config || {};
        if ("tableId" in config && config.tableId) {
          const tableId = config.tableId;
          addEdge(edges, assetIdsToNodeIds, sourceAssetId, tableId, warnings);
        }
        if ("storageId" in config && config.storageId) {
          const storageId = config.storageId;
          addEdge(edges, assetIdsToNodeIds, sourceAssetId, storageId, warnings);
        }
        if ("chatbotId" in config && config.chatbotId) {
          const chatbotId = config.chatbotId;
          addEdge(edges, assetIdsToNodeIds, sourceAssetId, chatbotId, warnings);
        }
        if ("triggers" in config && Array.isArray(config.triggers)) {
          for (const trigger of config.triggers) {
            if ("pageId" in trigger.config && trigger.config.pageId) {
              const targetPageId = trigger.config.pageId;
              const targetAssetId = `${interfaceId}.${targetPageId}`;
              addEdge(
                edges,
                assetIdsToNodeIds,
                sourceAssetId,
                targetAssetId,
                warnings,
              );
            }
          }
        }
        if ("children" in block) {
          const children = block.children || [];
          for (const child of children) {
            const config = child.config || {};
            if ("tableId" in config) {
              const tableId = config.tableId as string;
              addEdge(
                edges,
                assetIdsToNodeIds,
                sourceAssetId,
                tableId,
                warnings,
              );
            }
            if ("pageId" in config) {
              const targetPageId = config.pageId;
              const targetAssetId = `${interfaceId}.${targetPageId}`;
              addEdge(
                edges,
                assetIdsToNodeIds,
                sourceAssetId,
                targetAssetId,
                warnings,
              );
            }
          }
        }
      }
    }
  }
}

function addEdge(
  edges: CanvasData["canvas"]["edges"],
  assetIdsToNodeIds: Record<string, string>,
  sourceAssetId: string,
  targetAssetId: string,
  warnings: string[],
): void {
  const [sourceId, targetId] = replaceTemplateString(
    sourceAssetId,
    targetAssetId,
  );

  if (!assetIdsToNodeIds[sourceId]) {
    warnings.push(
      `Failed to add edge between ${sourceId} to ${targetId}: ${sourceId} does not exist`,
    );
    return;
  }

  if (!assetIdsToNodeIds[targetId]) {
    warnings.push(
      `Failed to add edge between ${sourceId} to ${targetId}: ${targetId} does not exist`,
    );
    return;
  }

  if (assetIdsToNodeIds[sourceId] === assetIdsToNodeIds[targetId]) {
    return;
  }

  const edge = {
    source: assetIdsToNodeIds[sourceId],
    target: assetIdsToNodeIds[targetId],
    id: `${assetIdsToNodeIds[sourceId]}=>${assetIdsToNodeIds[targetId]}`,
    markerEnd: {
      type: "arrow",
      strokeWidth: 1.5,
      color: "#64748B", // neutral700
    },
  };

  if (!edges.some((e) => e.id === edge.id)) {
    edges.push(edge);
  }
}

function replaceTemplateString(
  sourceAssetId: string,
  targetAssetId: string,
): [string, string] {
  const patterns = [
    /\${{\s*tables\.(\S+)\s*}}/,
    /\${{\s*chatbots\.(\S+)\s*}}/,
    /\${{\s*interfaces\.(\S+)\s*}}/,
  ];

  for (const pattern of patterns) {
    const sourceMatch = sourceAssetId.match(pattern);
    if (sourceMatch) {
      sourceAssetId = sourceMatch[1];
    }
    const targetMatch = targetAssetId.match(pattern);
    if (targetMatch) {
      targetAssetId = targetMatch[1];
    }
  }

  return [sourceAssetId, targetAssetId];
}

// Function to preserve specialized edges like zapStepEdge
function preserveSpecializedEdges(
  existingCanvas: CanvasData | undefined,
  newEdges: CanvasData["canvas"]["edges"],
  newNodes: CanvasData["canvas"]["nodes"],
): void {
  if (!existingCanvas?.canvas.edges) return;

  // Get all node IDs that exist in the new canvas
  const newNodeIds = new Set(newNodes.map((node) => node.id));

  // First, ensure all zapStep nodes are present for zap nodes
  const existingZapStepNodes = existingCanvas.canvas.nodes.filter(
    (node) => node.type === "zapStep",
  );

  for (const zapStepNode of existingZapStepNodes) {
    if (!newNodeIds.has(zapStepNode.id) && zapStepNode.parentId) {
      // Check if the parent zap node exists in the new canvas
      if (newNodeIds.has(zapStepNode.parentId)) {
        // Preserve the zapStep node
        newNodes.push({ ...zapStepNode });
        // Add the zapStep ID to the newNodeIds set
        newNodeIds.add(zapStepNode.id);
      }
    }
  }

  // Preserve specialized edges
  for (const edge of existingCanvas.canvas.edges) {
    // Only copy edges that aren't already accounted for in the new edges
    const edgeExists = newEdges.some((e) => e.id === edge.id);
    if (!edgeExists) {
      // Only preserve edges if both source and target nodes exist in the new canvas
      if (newNodeIds.has(edge.source) && newNodeIds.has(edge.target)) {
        // Clone the edge with all its properties
        const clonedEdge = { ...edge };
        newEdges.push(clonedEdge);
      }
    }
  }
}

// Function to restore edge styles from existing edges
function restoreEdgeStyles(
  edges: CanvasData["canvas"]["edges"],
  existingEdgeStyles: Record<string, any>,
): void {
  for (const edge of edges) {
    const styleKey = `${edge.source}=>${edge.target}`;
    if (existingEdgeStyles[styleKey]) {
      // Apply saved styles
      const savedStyle = existingEdgeStyles[styleKey];

      if (savedStyle.sourceHandle) {
        edge.sourceHandle = savedStyle.sourceHandle;
      }

      if (savedStyle.targetHandle) {
        edge.targetHandle = savedStyle.targetHandle;
      }

      if (savedStyle.markerEnd) {
        edge.markerEnd = { ...savedStyle.markerEnd };
      }
    }
  }
}

// Generic function to find a non-overlapping position for a new node
function findNonOverlappingPosition(
  nodes: CanvasData["canvas"]["nodes"],
  baseX: number,
  baseY: number,
  nodeWidth: number = 280,
  nodeHeight: number = 300,
): { x: number; y: number } {
  // Start with the base position
  const posX = baseX;
  let posY = baseY;

  const OVERLAP_MARGIN = 50; // Buffer space between nodes
  const MAX_ITERATIONS = 1000; // Safety net in case of unexpected overlap edge cases
  let iterations = 0;

  // Check for overlaps with existing nodes
  let hasOverlap = true;
  while (hasOverlap) {
    hasOverlap = false;
    iterations += 1;
    if (iterations > MAX_ITERATIONS) {
      break;
    }

    for (const existingNode of nodes) {
      if (existingNode.type === "text") continue;

      const existingX = existingNode.position?.x ?? 0;
      const existingY = existingNode.position?.y ?? 0;
      const existingWidth = existingNode.measured?.width ?? 280;
      const existingHeight = existingNode.measured?.height ?? 300;

      // Check if there's horizontal overlap
      const horizontalOverlap =
        Math.abs(posX - existingX) <
        (nodeWidth + existingWidth) / 2 + OVERLAP_MARGIN;

      // Only check vertical overlap if there's horizontal overlap
      if (horizontalOverlap) {
        // Use strict < so that when posY is exactly at the margin we no longer overlap (avoids infinite loop)
        const verticalOverlap =
          (posY >= existingY &&
            posY < existingY + existingHeight + OVERLAP_MARGIN) ||
          (posY + nodeHeight > existingY &&
            posY + nodeHeight < existingY + existingHeight + OVERLAP_MARGIN) ||
          (posY <= existingY &&
            posY + nodeHeight >= existingY + existingHeight);

        if (verticalOverlap) {
          // Move down below this node
          posY = existingY + existingHeight + OVERLAP_MARGIN;
          hasOverlap = true;
          break; // Restart check with new position
        }
      }
    }
  }

  return { x: posX, y: posY };
}
